import { Editor, Extension, JSONContent, Node } from "@tiptap/core";
import { cloneDeep } from "lodash-es";

export const findBlockById = (document: JSONContent, blockId: string): JSONContent | null => {
  for (const node of document?.content ?? []) {
    // Check if current node's blockId matches the target blockId
    if (node.attrs?.blockId === blockId) {
      return node;
    }
    // If not, and if current node has content (i.e., it can have children), look for the blockId in its children
    if (node.content) {
      const result = findBlockById(node, blockId);
      if (result) {
        return result;
      }
    }
  }
  // Return null if the blockId isn't found in this subtree
  return null;
};

export const findTopLevelParentByBlockId = (content: JSONContent, blockId: string): any => {
  for (let block of content?.content ?? []) {
    if (block?.attrs?.blockId === blockId) {
      return block;
    } else if (block.content) {
      const result = findBlockById(block.content, blockId);
      if (result) {
        return block;
      }
    }
  }
  return null;
};

export const updateBlockContent = (
  original: JSONContent,
  newData: JSONContent[],
  targetBlockId: string
): JSONContent | null => {
  const originalClone = cloneDeep(original);
  const newDataClone = cloneDeep(newData);

  function findAndReplace(node: JSONContent) {
    if (node?.attrs?.blockId === targetBlockId) {
      node.content = newDataClone;
      return true;
    }

    if (node.content) {
      for (const subNode of node.content) {
        if (findAndReplace(subNode)) return true;
      }
    }

    return false;
  }

  for (const block of originalClone?.content || []) {
    if (findAndReplace(block)) return originalClone;
  }

  return null;
};

export const findParentBlock = (
  content: JSONContent,
  blockId: string,
  parent: JSONContent | null = null
): JSONContent | null => {
  for (let block of content?.content ?? []) {
    if (block?.attrs?.blockId === blockId) {
      return parent;
    } else if (block.content) {
      const result = findParentBlock(block, blockId, block);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export interface Breadcrumbs {
  id: string;
  text?: string;
}

export const getBlockBreadcrumbs = (content: JSONContent, targetBlockId: string): Breadcrumbs[] => {
  let parentBlock = findParentBlock(content, targetBlockId);
  const breadcrumbs: Breadcrumbs[] = [];
  while (parentBlock) {
    // console.log("parentBlock", parentBlock);
    parentBlock?.content?.forEach((block) => {
      // console.log("hi", block);
      if (block?.type == "paragraph") {
        const parent = findParentBlock(content, block?.attrs?.blockId);
        breadcrumbs.unshift({
          id: parent?.attrs?.blockId,
          text: block?.content?.[0]?.text,
        });
      }
    });
    parentBlock = findParentBlock(content, parentBlock?.attrs?.blockId);
  }
  return [{ id: "", text: "Document Root" }, ...breadcrumbs];
};

export const removeNodesById = (content: JSONContent[], nodeIds: string[]): JSONContent[] => {
  return content.reduce<JSONContent[]>((acc, node) => {
    // If the current node's ID is in the list, skip adding it to the accumulator
    if (node.attrs && nodeIds.includes(node.attrs.blockId)) {
      return acc;
    }

    // If the node has children, recursively apply the same function to them
    if (node.content) {
      node = { ...node, content: removeNodesById(node.content, nodeIds) };
    }

    // Add the current node to the accumulator
    return acc.concat(node);
  }, []);
};
